import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconArrowRight, IconBulb, IconCheck, IconClock, IconLoader2, IconSparkles, IconX } from '@tabler/icons-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import contestService from '../../services/contestService';
import { notifications } from '../../lib/notifications';
import { BADGE_DAILY, CHIP, useNumFmt } from './contestUi';

// The contest's daily quiz (backend: config/contestQuiz.js + services/ContestQuizService.js). Contest points, NOT credits.
// Each owner is offered a couple of multiple-choice questions a day about the platform; a correct answer pays points at once, a wrong
// one pays nothing and takes nothing, and a question is answered once, ever. The list row opens a window with today's question:
// pick an option → "submit" → right / wrong (the right option is shown) → the next one, or the end of today's set. The window can be
// closed at any moment without answering — the questions simply stay for later in the day. The correct answer never reaches the page
// before the owner has answered (the server checks it).
// NB: the global `p { font-weight: 400 !important }` strips bold from <p> — bold text uses span/div here.

// Milliseconds until `nextAt` (an ISO time); re-read every 30 s so the wait text counts down and the row opens by itself.
function useWaitMs(nextAt) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!nextAt) return undefined;
        const id = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(id);
    }, [nextAt]);
    const ms = nextAt ? new Date(nextAt).getTime() - now : 0;
    return ms > 0 ? ms : 0;
}

// "২ ঘণ্টা ১০ মিনিট" / "১০ মিনিট"
function useWaitText(ms) {
    const { t } = useTranslation();
    const fmt = useNumFmt();
    const mins = Math.max(1, Math.ceil(ms / 60000));
    const h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? t('contest.quiz.hoursMin', { h: fmt(h), m: fmt(m) }) : t('contest.quiz.minOnly', { m: fmt(m) });
}

// ── Celebration / "oops" effects ─────────────────────────────────────────────────────────────────────────────────────────
// A right answer bursts colourful confetti over the whole screen; a wrong one flashes red, rains a few grey-red drops and shakes the
// window. Pure CSS (keyframes below), drawn in a portal so the dialog's transform doesn't pin it, and switched off for people who
// ask the system for reduced motion.
const FX_CSS = `
@keyframes quizBurst{0%{transform:translate(0,0) scale(.3) rotate(0);opacity:1}55%{transform:translate(var(--dx),var(--dy)) scale(1) rotate(var(--rot));opacity:1}100%{transform:translate(calc(var(--dx)*1.1),calc(var(--dy) + 32vh)) scale(.9) rotate(calc(var(--rot)*2));opacity:0}}
@keyframes quizDrop{0%{transform:translateY(-6vh);opacity:0}15%{opacity:.9}100%{transform:translateY(105vh);opacity:0}}
@keyframes quizPop{0%{transform:scale(.8);opacity:0}60%{transform:scale(1.05);opacity:1}100%{transform:scale(1);opacity:1}}
@keyframes quizShake{10%,90%{transform:translateX(-2px)}20%,80%{transform:translateX(4px)}30%,50%,70%{transform:translateX(-7px)}40%,60%{transform:translateX(7px)}}
@keyframes quizUp{0%{transform:translateY(10px);opacity:0}100%{transform:translateY(0);opacity:1}}
@keyframes quizGlow{0%,100%{box-shadow:0 0 0 0 rgba(230,138,0,.45)}50%{box-shadow:0 0 0 7px rgba(230,138,0,0)}}
@keyframes quizShine{0%{transform:translateX(-120%)}60%,100%{transform:translateX(220%)}}
@keyframes quizFlash{0%{opacity:0}18%{opacity:1}100%{opacity:0}}
@media (prefers-reduced-motion: reduce){.quiz-fx{display:none!important}.quiz-anim{animation:none!important}}`;
const CONFETTI = ['#E68A00', '#10b981', '#3b82f6', '#ec4899', '#facc15', '#8b5cf6', '#06b6d4', '#ef4444'];

function Effects({ kind, burst }) {
    const pieces = useMemo(() => {
        const rnd = (a, b) => a + Math.random() * (b - a);
        if (kind === 'right') {
            return Array.from({ length: 90 }, (_, i) => {
                const ang = rnd(0, Math.PI * 2), dist = rnd(18, 55);
                return { i, c: CONFETTI[i % CONFETTI.length], size: rnd(6, 12), round: i % 3 !== 0, dx: `${Math.cos(ang) * dist}vw`, dy: `${Math.sin(ang) * dist * 0.8 - 8}vh`, rot: `${rnd(-540, 540)}deg`, dur: rnd(1.5, 2.5), delay: rnd(0, 0.18) };
            });
        }
        return Array.from({ length: 26 }, (_, i) => ({ i, c: i % 2 ? '#ef4444' : '#a1a1aa', size: rnd(5, 9), left: `${rnd(3, 97)}vw`, dur: rnd(1.3, 2.2), delay: rnd(0, 0.5) }));
    }, [kind, burst]);
    if (typeof document === 'undefined') return null;
    return createPortal(
        <div className="quiz-fx pointer-events-none fixed inset-0 z-[300] overflow-hidden" aria-hidden="true">
            {kind === 'wrong' && <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(239,68,68,0) 45%, rgba(239,68,68,.32) 100%)', animation: 'quizFlash .9s ease-out forwards' }} />}
            {kind === 'right' && <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 45%, rgba(16,185,129,.18), rgba(16,185,129,0) 60%)', animation: 'quizFlash 1s ease-out forwards' }} />}
            {kind === 'right'
                ? pieces.map((p) => (
                    <span key={p.i} className="absolute" style={{ left: '50%', top: '42%', width: p.size, height: p.round ? p.size : p.size * 0.55, borderRadius: p.round ? '9999px' : 2, background: p.c, '--dx': p.dx, '--dy': p.dy, '--rot': p.rot, animation: `quizBurst ${p.dur}s cubic-bezier(.2,.7,.3,1) ${p.delay}s forwards`, opacity: 0 }} />
                ))
                : pieces.map((p) => (
                    <span key={p.i} className="absolute top-0 rounded-full" style={{ left: p.left, width: p.size, height: p.size * 1.6, background: p.c, animation: `quizDrop ${p.dur}s ease-in ${p.delay}s forwards`, opacity: 0 }} />
                ))}
        </div>,
        document.body,
    );
}

const LETTERS = { bn: ['ক', 'খ', 'গ', 'ঘ'], en: ['A', 'B', 'C', 'D'] };

// One option card: idle → hover tint; picked → brand tint; after the answer: the right one green, a wrong pick red, the rest dimmed.
const OPTION = {
    idle: 'border-zinc-200 bg-white hover:border-primary/60 hover:bg-primary/5 dark:border-zinc-700 dark:bg-zinc-800 dark:hover:bg-primary/10',
    picked: 'border-primary bg-primary/10 dark:bg-primary/15',
    correct: 'border-emerald-500 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-900/20',
    wrong: 'border-red-400 bg-red-50 dark:border-red-500 dark:bg-red-900/20',
    dim: 'border-zinc-200 bg-white opacity-60 dark:border-zinc-700 dark:bg-zinc-800',
};
const LETTER = {
    idle: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300',
    picked: 'bg-primary text-white',
    correct: 'bg-emerald-500 text-white',
    wrong: 'bg-red-500 text-white',
    dim: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400',
};

function QuizWindow({ open, onClose }) {
    const { t, currentLanguage } = useTranslation();
    const fmt = useNumFmt();
    const letters = LETTERS[currentLanguage === 'bn' ? 'bn' : 'en'];

    const [quiz, setQuiz] = useState(null);         // GET /quiz: the summary + today's questions
    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [picked, setPicked] = useState(null);      // the option id chosen but not yet sent
    const [result, setResult] = useState(null);      // after an answer: { correct, correctOptionId, points, quiz, question, optionId }
    const [busy, setBusy] = useState(false);
    const [fx, setFx] = useState(null);              // { kind: 'right' | 'wrong', id } while the celebration / oops effect plays
    const answeredRef = useRef(false);               // anything answered since the window opened → the page re-reads its totals on close

    const load = useCallback(async () => {
        setStatus('loading');
        try {
            setQuiz(await contestService.getQuiz());
            setStatus('ready');
        } catch {
            setStatus('error');
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        setPicked(null);
        setResult(null);
        setFx(null);
        answeredRef.current = false;
        load();
    }, [open, load]);

    useEffect(() => {
        if (!fx) return undefined;
        const id = setTimeout(() => setFx(null), 3000);
        return () => clearTimeout(id);
    }, [fx]);

    const question = quiz?.questions?.[0] || null;

    // 1–4 pick an option, Enter sends the answer (only while a question is open and not answered yet)
    useEffect(() => {
        if (!open || !question || result) return undefined;
        const onKey = (e) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const n = Number(e.key);
            if (n >= 1 && n <= question.options.length) setPicked(question.options[n - 1].id);
            else if (e.key === 'Enter' && picked !== null && !busy) { e.preventDefault(); submit(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });
    const waitMs = useWaitMs(result ? null : quiz?.nextAt);
    const waitText = useWaitText(waitMs);
    // "Today's question 1/2" counts the answers already given today; it stays on the question just answered until "next".
    const current = (quiz?.answeredToday ?? 0) + 1;

    const submit = async () => {
        if (picked === null || busy || !question) return;
        setBusy(true);
        try {
            const res = await contestService.answerQuiz({ questionId: question.id, optionId: picked });
            answeredRef.current = true;
            setResult({ ...res, question, optionId: picked });
            setFx({ kind: res.correct ? 'right' : 'wrong', id: Date.now() });
        } catch (e) {
            // 409 = the server has already settled it (answered / today's set is done); 400 = the question is no longer on offer
            // (a new day began, or the contest ended while the window was open). Either way show what is on offer now.
            const settled = e?.code === 'ALREADY_ANSWERED' || e?.code === 'QUIZ_DONE_TODAY';
            const stale = settled || e?.code === 'QUIZ_WAIT' || e?.status === 400;
            const key = e?.code === 'ALREADY_ANSWERED' ? 'errAnswered' : e?.code === 'QUIZ_DONE_TODAY' ? 'errDoneToday' : e?.code === 'QUIZ_WAIT' ? 'errWait' : e?.status === 400 ? 'errStale' : 'errFailed';
            notifications.show({ message: t(`contest.quiz.${key}`), color: 'red' });
            if (settled) answeredRef.current = true;
            if (stale) { setPicked(null); load(); }
        } finally {
            setBusy(false);
        }
    };

    const next = () => { setQuiz(result.quiz); setResult(null); setPicked(null); };
    const stateOf = (id) => {
        if (!result) return picked === id ? 'picked' : 'idle';
        if (id === result.correctOptionId) return 'correct';
        if (id === result.optionId) return 'wrong';
        return 'dim';
    };

    // What the window says when there is no question to show.
    const emptyText = !quiz ? '' : quiz.enabled === false ? t('contest.quiz.notAvailable') : quiz.finished ? t('contest.quiz.allDone') : waitMs > 0 ? t('contest.quiz.waitMsg') : t('contest.quiz.doneForToday', { n: fmt(quiz.perDay) });
    const after = result?.quiz; // the state after the answer just given: is there another question today?
    const moreToday = (after?.questions?.length ?? 0) > 0;
    const afterWaitMs = after?.nextAt ? Math.max(0, new Date(after.nextAt).getTime() - Date.now()) : 0;
    const afterWaitText = useWaitText(afterWaitMs);
    const endText = !after ? '' : after.finished ? t('contest.quiz.allDone') : afterWaitMs > 0 ? t('contest.quiz.waitMsg') : t('contest.quiz.doneForToday', { n: fmt(after.perDay) });

    return (
        <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(answeredRef.current); }}>
            {fx && <Effects kind={fx.kind} burst={fx.id} />}
            <DialogContent
                showClose={false}
                // above the floating help / WhatsApp buttons (z-100 / z-190), like the video window
                className="z-[200] max-h-[calc(100dvh-24px)] w-[calc(100vw-24px)] max-w-[520px] gap-0 overflow-y-auto rounded-2xl border border-zinc-200 p-0 font-hind dark:border-zinc-700"
                overlayClassName="z-[199]"
            >
                <style>{FX_CSS}</style>
                <div className="flex items-center gap-3.5 border-b border-zinc-100 bg-gradient-to-r from-primary/10 via-amber-50/60 to-transparent px-5 py-4 dark:border-zinc-800 dark:from-primary/15 dark:via-transparent">
                    <span className="quiz-anim flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary text-white shadow-sm" style={{ animation: 'quizGlow 2.2s ease-in-out infinite' }}><IconBulb size={22} /></span>
                    <div className="min-w-0 flex-1">
                        <DialogTitle className="text-[18px] font-bold leading-snug text-zinc-900 dark:text-zinc-100">{t('contest.quiz.title')}</DialogTitle>
                        <DialogDescription className="mt-0.5 text-[13px] leading-snug text-zinc-500 dark:text-zinc-400">
                            {question && quiz
                                ? t('contest.quiz.progress', { n: fmt(current), of: fmt(quiz.perDay), done: fmt(quiz.answered), total: fmt(quiz.total) })
                                : t('contest.quiz.subtitle')}
                        </DialogDescription>
                    </div>
                    <DialogClose aria-label={t('common.close')} className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-zinc-100 text-zinc-600 transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700">
                        <IconX size={18} />
                    </DialogClose>
                </div>

                <div className="quiz-anim px-5 py-5" key={fx?.kind === 'wrong' ? `w${fx.id}` : 'n'} style={fx?.kind === 'wrong' ? { animation: 'quizShake .55s ease-in-out' } : undefined}>
                    {status === 'loading' && (
                        <div className="flex flex-col gap-3" role="status" aria-label={t('common.loading')}>
                            <div className="h-5 w-4/5 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800" />
                            {[0, 1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-zinc-100 dark:bg-zinc-800" />)}
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="flex flex-col items-start gap-3">
                            <span className="text-[15px] text-zinc-600 dark:text-zinc-300">{t('contest.quiz.loadFailed')}</span>
                            <button type="button" onClick={load} className="inline-flex h-10 items-center rounded-[10px] bg-primary px-4 text-sm font-semibold text-white hover:bg-primary/90">{t('contest.quiz.retry')}</button>
                        </div>
                    )}

                    {status === 'ready' && !question && (
                        <div className="flex flex-col items-start gap-4">
                            <span className="text-[15px] leading-relaxed text-zinc-600 dark:text-zinc-300">{emptyText}</span>
                            <DialogClose className="inline-flex h-11 items-center rounded-[10px] bg-primary px-5 text-sm font-semibold text-white hover:bg-primary/90">{t('contest.quiz.close')}</DialogClose>
                        </div>
                    )}

                    {status === 'ready' && question && (
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-1.5" aria-hidden="true">
                                {Array.from({ length: quiz.perDay }, (_, i) => (
                                    <span key={i} className={cn('h-1.5 flex-1 rounded-full transition-colors duration-500', i < (result ? current : current - 1) ? 'bg-emerald-500' : i === current - 1 ? 'bg-primary' : 'bg-zinc-200 dark:bg-zinc-700')} />
                                ))}
                                <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"><IconSparkles size={12} />+{fmt(quiz.pointsPerAnswer)}</span>
                            </div>
                            <div key={question.id} className="quiz-anim text-[17px] font-semibold leading-relaxed text-zinc-900 dark:text-zinc-100" style={{ animation: 'quizUp .4s ease-out both' }}>{question.text}</div>

                            <div role="radiogroup" aria-label={question.text} className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                                {question.options.map((o, i) => {
                                    const st = stateOf(o.id);
                                    return (
                                        <button
                                            key={o.id}
                                            type="button"
                                            role="radio"
                                            aria-checked={picked === o.id || result?.optionId === o.id}
                                            disabled={busy || !!result}
                                            onClick={() => setPicked(o.id)}
                                            style={{ animation: `quizUp .35s ease-out ${0.08 + i * 0.07}s both` }}
                                            className={cn('quiz-anim flex min-h-[56px] w-full items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition-all duration-200 disabled:cursor-default', !result && 'hover:-translate-y-0.5 hover:shadow-md active:scale-[.98]', st === 'picked' && 'scale-[1.02] shadow-md', OPTION[st])}
                                        >
                                            <span className={cn('flex h-7 w-7 flex-none items-center justify-center rounded-full text-[13px] font-bold', LETTER[st])}>
                                                {st === 'correct' || st === 'picked' ? <IconCheck size={15} stroke={3} /> : st === 'wrong' ? <IconX size={15} stroke={3} /> : letters[i]}
                                            </span>
                                            <span className="min-w-0 flex-1 text-[15px] leading-snug text-zinc-800 dark:text-zinc-100">{o.text}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {!result && (
                                <button
                                    type="button"
                                    disabled={picked === null || busy}
                                    onClick={submit}
                                    className="group relative inline-flex h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-primary to-amber-500 text-[15px] font-bold text-white shadow-sm transition-all hover:shadow-md active:scale-[.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                                >
                                    {picked !== null && !busy && <span className="quiz-anim pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-white/30 blur-md" style={{ animation: 'quizShine 2s ease-in-out infinite' }} />}
                                    {busy ? <IconLoader2 size={16} className="animate-spin" /> : null}
                                    {t('contest.quiz.submit')}
                                    {!busy && <IconArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />}
                                </button>
                            )}

                            {result && (
                                <>
                                    <div style={{ animation: 'quizPop .45s cubic-bezier(.2,.9,.3,1.2) both' }} className={cn('quiz-anim flex items-start gap-2.5 rounded-xl px-3.5 py-3', result.correct ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300')}>
                                        <span className="mt-0.5 flex-none">{result.correct ? <IconCheck size={18} stroke={2.6} /> : <IconX size={18} stroke={2.6} />}</span>
                                        <div className="flex min-w-0 flex-col gap-0.5">
                                            <span className="text-[15px] font-semibold">{result.correct ? t('contest.quiz.correct', { n: fmt(result.points) }) : t('contest.quiz.wrong')}</span>
                                            {!result.correct && (
                                                <span className="text-[13px] leading-relaxed">
                                                    {t('contest.quiz.rightAnswer', { text: result.question.options.find((o) => o.id === result.correctOptionId)?.text || '' })}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {moreToday ? (
                                        <button type="button" onClick={next} className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-primary text-[15px] font-bold text-white transition-colors hover:bg-primary/90">{t('contest.quiz.next')}</button>
                                    ) : (
                                        <div className="flex flex-col gap-3">
                                            <span className="text-[14px] leading-relaxed text-zinc-500 dark:text-zinc-400">{endText}</span>
                                            <DialogClose className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-primary text-[15px] font-bold text-white transition-colors hover:bg-primary/90">{t('contest.quiz.close')}</DialogClose>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * The quiz's row in the bonus-task list. `quiz` = data.quiz of GET /api/contest/me (the progress summary — no questions); the row is
 * not drawn when there is none (no live contest / not an owner) or when this owner has answered every question. `onChanged` re-reads
 * the page totals once the window is closed after an answer.
 */
export default function ContestQuizRow({ quiz, onChanged }) {
    const { t } = useTranslation();
    const fmt = useNumFmt();
    const [open, setOpen] = useState(false);
    const waitMs = useWaitMs(quiz?.nextAt);
    const waitText = useWaitText(waitMs);
    if (!quiz?.enabled || quiz.finished) return null;

    const left = quiz.remainingToday;
    const doneToday = left === 0;
    const waiting = !doneToday && waitMs > 0;
    const close = (answered) => {
        setOpen(false);
        if (answered && onChanged) onChanged();
    };

    return (
        <li className={`rounded-[14px] border border-zinc-100 dark:border-zinc-800 ${doneToday ? 'bg-zinc-50 dark:bg-zinc-800/40' : 'bg-white dark:bg-zinc-900'}`}>
            <div className="flex flex-wrap items-center gap-3.5 py-3 pl-3.5 pr-3">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-[11px] bg-primary/10 text-primary dark:bg-primary/20"><IconBulb size={18} /></span>
                <div className="flex min-w-0 flex-[1_1_200px] flex-col">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{t('contest.quiz.title')}</span>
                        <span className="inline-flex items-center gap-2">
                            <span className="text-[15px] text-zinc-400 dark:text-zinc-500" aria-hidden="true">-</span>
                            <span className={BADGE_DAILY}>{t('contest.quiz.badge', { n: fmt(quiz.perDay) })}</span>
                        </span>
                    </span>
                    <span className="mt-1 text-[13px] text-zinc-500 dark:text-zinc-400">
                        {doneToday ? t('contest.quiz.rowDone') : waiting ? t('contest.quiz.rowWait') : t('contest.quiz.rowHint', { n: fmt(left) })}
                    </span>
                </div>
                <div className="ml-auto flex flex-none items-center gap-2.5">
                    <span className={`font-baloo text-base font-bold ${doneToday || waiting ? 'text-zinc-400 dark:text-zinc-500' : 'text-emerald-700 dark:text-emerald-400'}`}>+{fmt(quiz.pointsPerAnswer)}</span>
                    {waiting ? (
                        <span className={`${CHIP} bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300`}><IconClock size={14} stroke={2.2} />{t('contest.quiz.chipWait')}</span>
                    ) : doneToday ? (
                        <span className={`${CHIP} bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300`}><IconCheck size={14} stroke={2.4} />{t('contest.quiz.chipDone')}</span>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="inline-flex h-[38px] min-w-[104px] items-center justify-center gap-1.5 rounded-[10px] bg-primary px-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                        >
                            {t('contest.quiz.answerNow')}
                        </button>
                    )}
                </div>
            </div>
            <QuizWindow open={open} onClose={close} />
        </li>
    );
}
